/**
 * MentalHealthIndexCard
 * =====================
 * Displays the AI-generated Mental Health Interaction Index in the
 * Current Visit > Summary sub-tab of the Right Panel.
 *
 * States:
 *   not-generated  → "Generate" button
 *   generating     → spinner
 *   draft          → full card + Review / Edit / Discard buttons
 *   validated      → full card (read-only, green badge) + Regenerate button
 *   discarded      → collapsed notice + Regenerate button
 */
import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Slider,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  AutoAwesome,
  CheckCircle,
  Delete,
  Edit,
  ExpandMore,
  ExpandLess,
  Refresh,
  Warning,
} from '@mui/icons-material';
import type { MHIFactor, MHIPayload, MHIRecord } from '../../hooks/useMentalHealthIndex';
import { useMentalHealthIndex } from '../../hooks/useMentalHealthIndex';

// ──────────────────────────────────────────────────────────────────────────────
// COLOUR HELPERS
// ──────────────────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score <= 20) return '#4caf50';   // green
  if (score <= 45) return '#ff9800';   // amber
  if (score <= 70) return '#f44336';   // red
  return '#b71c1c';                    // dark red
}

function statusColor(status: string): 'default' | 'success' | 'error' | 'warning' {
  if (status === 'validated') return 'success';
  if (status === 'discarded') return 'error';
  return 'warning';
}

// ──────────────────────────────────────────────────────────────────────────────
// FACTOR MINI-METER
// ──────────────────────────────────────────────────────────────────────────────

interface FactorRowProps {
  label: string;
  weight: string;
  factor: MHIFactor;
  editMode: boolean;
  onChange?: (score: number) => void;
}

function FactorRow({ label, weight, factor, editMode, onChange }: FactorRowProps) {
  const color = scoreColor(factor.score);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
          {label}
          <Typography component="span" variant="caption" sx={{ color: '#888', ml: 0.5 }}>
            ({weight})
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={factor.label}
            size="small"
            sx={{ height: 18, fontSize: '10px', bgcolor: color + '22', color }}
          />
          <Typography variant="caption" sx={{ fontWeight: 700, color, minWidth: 26, textAlign: 'right' }}>
            {factor.score}
          </Typography>
        </Box>
      </Box>
      {editMode && onChange ? (
        <Slider
          size="small"
          value={factor.score}
          onChange={(_, v) => onChange(v as number)}
          min={0}
          max={100}
          sx={{ color, py: 0.5 }}
        />
      ) : (
        <LinearProgress
          variant="determinate"
          value={factor.score}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: '#e0e0e0',
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
          }}
        />
      )}
      {factor.signals.length > 0 && !editMode && (
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.2 }}>
          {factor.signals.slice(0, 2).join(' · ')}
        </Typography>
      )}
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

interface Props {
  visitId: number | null;
  sessionId?: string | null;
}

export default function MentalHealthIndexCard({ visitId, sessionId }: Props) {
  const { record, loading, generating, error, generate, validate, discard } =
    useMentalHealthIndex(visitId, sessionId);

  const [expanded, setExpanded] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editPayload, setEditPayload] = useState<MHIPayload | null>(null);

  if (!visitId) return null;

  // ── Loading skeleton ──
  if (loading && !record) {
    return (
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading Behaviour Wellness Index…
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // ── Not generated yet ──
  if (!record) {
    return (
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, borderStyle: 'dashed' }}>
        <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            Behaviour Wellness & Impact Index not yet generated for this visit.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesome sx={{ fontSize: 16 }} />}
            onClick={generate}
            disabled={generating}
            sx={{ textTransform: 'none' }}
          >
            {generating ? 'Analysing transcript…' : 'Generate Behaviour Wellness Index'}
          </Button>
          {error && (
            <Alert severity="error" sx={{ mt: 1, textAlign: 'left' }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── Discarded state ──
  if (record.status === 'discarded') {
    return (
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, borderColor: '#e0e0e0' }}>
        <CardContent sx={{ py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete sx={{ fontSize: 16, color: '#9e9e9e' }} />
            <Typography variant="caption" color="text.secondary">
              Behaviour Wellness & Impact Index discarded by clinician.
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={generating ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 14 }} />}
            onClick={generate}
            disabled={generating}
            sx={{ textTransform: 'none', fontSize: '11px' }}
          >
            Regenerate
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ── Active card (draft or validated) ──
  const payload: MHIPayload = editMode && editPayload ? editPayload : record.payload;
  const overallColor = scoreColor(payload.overall_score);
  const hasRiskFlags = (payload.risk_review?.flags || []).length > 0;

  const updateFactor = (key: keyof MHIPayload, score: number) => {
    if (!editPayload) return;
    const updated = {
      ...editPayload,
      [key]: { ...(editPayload[key] as MHIFactor), score },
    };
    const overall = Math.round(
      (updated.emotional_distress as MHIFactor).score * 0.30 +
      (updated.affect_observed as MHIFactor).score * 0.20 +
      (updated.symptom_burden as MHIFactor).score * 0.30 +
      (updated.risk_review as MHIFactor).score * 0.20
    );
    setEditPayload({ ...updated, overall_score: overall });
  };

  const enterEdit = () => {
    setEditPayload({ ...record.payload });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditPayload(null);
  };

  const saveEdit = async () => {
    await validate(editPayload ?? undefined);
    setEditMode(false);
    setEditPayload(null);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        borderColor: record.status === 'validated' ? '#4caf50' : '#e0e0e0',
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome sx={{ fontSize: 16, color: '#5c6bc0' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#012676' }}>
              Behaviour Wellness & Impact Index
            </Typography>
            <Chip
              label={record.status.toUpperCase()}
              size="small"
              color={statusColor(record.status)}
              sx={{ height: 18, fontSize: '10px' }}
            />
            {payload.method !== 'llm' && (
              <Tooltip title={`Generated via ${payload.method} (LLM unavailable)`}>
                <Chip
                  label={payload.method.toUpperCase()}
                  size="small"
                  sx={{ height: 18, fontSize: '10px', bgcolor: '#fff3e0', color: '#e65100' }}
                />
              </Tooltip>
            )}
          </Box>
          <IconButton size="small" onClick={() => setExpanded((p) => !p)}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Box>

        {/* Overall score meter */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#333' }}>
              Overall Score
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={payload.overall_label}
                size="small"
                sx={{ height: 20, fontSize: '11px', bgcolor: overallColor + '22', color: overallColor, fontWeight: 700 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 800, color: overallColor, minWidth: 32, textAlign: 'right' }}>
                {payload.overall_score} / 100
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={payload.overall_score}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': { bgcolor: overallColor, borderRadius: 5 },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
            <Typography variant="caption" sx={{ color: '#888', fontSize: '10px' }}>No concern</Typography>
            <Typography variant="caption" sx={{ color: '#888', fontSize: '10px' }}>High concern</Typography>
          </Box>
        </Box>

        {/* High-risk flag alert */}
        {hasRiskFlags && (
          <Alert severity="error" icon={<Warning fontSize="small" />} sx={{ mb: 1, py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Risk signals detected: {(payload.risk_review.flags || []).join(', ')}
            </Typography>
          </Alert>
        )}

        <Collapse in={expanded}>
          <Divider sx={{ my: 1 }} />

          {/* Factor mini-meters */}
          <FactorRow
            label="Emotional Distress"
            weight="30%"
            factor={payload.emotional_distress}
            editMode={editMode}
            onChange={(v) => updateFactor('emotional_distress', v)}
          />
          <FactorRow
            label="Symptom Burden"
            weight="30%"
            factor={payload.symptom_burden}
            editMode={editMode}
            onChange={(v) => updateFactor('symptom_burden', v)}
          />
          <FactorRow
            label="Affect Observed"
            weight="20%"
            factor={payload.affect_observed}
            editMode={editMode}
            onChange={(v) => updateFactor('affect_observed', v)}
          />
          <FactorRow
            label="Risk Review"
            weight="20%"
            factor={payload.risk_review}
            editMode={editMode}
            onChange={(v) => updateFactor('risk_review', v)}
          />

          {/* Key Drivers */}
          {payload.key_drivers?.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#444', display: 'block', mb: 0.5 }}>
                Key Contributing Drivers
              </Typography>
              <List dense disablePadding>
                {payload.key_drivers.map((d, i) => (
                  <ListItem key={i} sx={{ py: 0, pl: 1 }}>
                    <ListItemText
                      primary={d}
                      primaryTypographyProps={{ variant: 'caption', color: '#555' }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {/* Disclaimer */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color: '#9e9e9e',
              fontStyle: 'italic',
              fontSize: '10px',
            }}
          >
            ⚠ Assistive AI tool. Does not auto-finalize diagnosis. Clinical judgment must prevail.
          </Typography>

          {/* Confidence badge */}
          <Box sx={{ mt: 0.5 }}>
            <Chip
              label={`Confidence: ${payload.confidence}`}
              size="small"
              sx={{ height: 16, fontSize: '10px', bgcolor: '#f5f5f5', color: '#757575' }}
            />
          </Box>
        </Collapse>

        {/* Action buttons */}
        <Divider sx={{ my: 1 }} />
        {editMode ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircle sx={{ fontSize: 14 }} />}
              onClick={saveEdit}
              disabled={loading}
              sx={{ textTransform: 'none', fontSize: '12px' }}
            >
              Save & Validate
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={cancelEdit}
              sx={{ textTransform: 'none', fontSize: '12px' }}
            >
              Cancel
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {record.status === 'draft' && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle sx={{ fontSize: 14 }} />}
                  onClick={() => validate()}
                  disabled={loading}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                >
                  Validate
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit sx={{ fontSize: 14 }} />}
                  onClick={enterEdit}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Delete sx={{ fontSize: 14 }} />}
                  onClick={discard}
                  disabled={loading}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                >
                  Discard
                </Button>
                <Tooltip title="Re-analyse transcript (use when consultation is nearly complete)">
                  <span>
                    <IconButton
                      size="small"
                      onClick={generate}
                      disabled={generating}
                      sx={{ ml: 'auto' }}
                    >
                      {generating ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {record.status === 'validated' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircle sx={{ fontSize: 14, color: '#4caf50' }} />
                  <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                    Validated
                    {record.validated_at ? ` · ${new Date(record.validated_at).toLocaleTimeString()}` : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={generating ? <CircularProgress size={12} /> : <Refresh sx={{ fontSize: 14 }} />}
                  onClick={generate}
                  disabled={generating}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                >
                  Regenerate
                </Button>
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
